import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeCampaignResponse } from '@nkr/store/index';
import { IBaseResponse } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Panel, Row, Well } from 'react-bootstrap';
import CampaignSteps from './campaign-steps';
import { FormWrapper } from '../styles';
import { AxiosError } from 'axios';
import { TableWrapper } from './styles';
import { toast } from 'react-toastify';

const columnMap = [
    'First Name',
    'Last Name',
    'Address Line 1',
    'Address Line 2',
    'City',
    'State',
    'Postal Code'
];

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    saveCampaignMailingListMapping: (id: number, hasColumnHeaders: boolean, columns: string[]) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    id: number,
    campaignName: string,
    startDate: string,
    endDate: string,
    processedByNkr: boolean,
    hasColumnHeaders: boolean,
    columnsFromFile: string[],
    sampleRow: string[],
    assignedColumns: string[]
}

class CampaignMailingListMapping extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        campaignName: '',
        startDate: '',
        endDate: '',
        processedByNkr: true,
        hasColumnHeaders: false,
        columnsFromFile: [],
        sampleRow: [],
        assignedColumns: columnMap.map(() => '')
    }

    componentDidMount = () => {
        this.props.showLoading('Getting the campaign, one moment please...').then(() => {
            this.props.getCampaign(this.props.campaignId).then(response => {
                if (response.data.success) {
                    let { id, campaignName, startDate, endDate, processedByNkr, mailingListHasColumnHeaders: hasColumnHeaders, mailingListColumnMapping, columns: columnsFromFile, sampleRow } = response.data.results;

                    let assignedColumns = mailingListColumnMapping.length == columnMap.length ? mailingListColumnMapping : columnMap.map(() => '');

                    this.setState({
                        id,
                        campaignName,
                        startDate,
                        endDate,
                        processedByNkr,
                        hasColumnHeaders,
                        columnsFromFile,
                        sampleRow,
                        assignedColumns
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    handleSubmit = (continueNextStep: boolean) => {
        this.props.showLoading('Saving mailing list mapping, one moment please...').then(() => {
            this.props.saveCampaignMailingListMapping(this.state.id, this.state.hasColumnHeaders, this.state.assignedColumns)
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Mailing list mapping saved successfully, going to the confirmation step...', {
                            onClose: () => {
                                window.location.href = continueNextStep ? this.getNextUrl() : this.getReturnUrl();
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                    toast.error('Unable to save the mailing list mapping, the following error occurred: ' + error.message);
                });
        });
    }

    handleHasColumnHeadersChange = (hasColumnHeaders: boolean) => {
        let assignedColumns = [...this.state.assignedColumns];

        if (hasColumnHeaders) {
            assignedColumns = columnMap.map((column, index) => {
                if (this.state.columnsFromFile[index] == column)
                    return column;
                return '';
            });
        }

        this.setState({
            hasColumnHeaders,
            assignedColumns
        });
    }

    handleAssignedColumnChange = (index: number, value: string) => {
        let assignedColumns = [...this.state.assignedColumns];

        const itemIndex = assignedColumns.findIndex(element => element == value);
        if(itemIndex >=0)
            assignedColumns[itemIndex] = "-1";       
        assignedColumns[index] = value;
    
        this.setState({
            assignedColumns
        });
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    getNextUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaign/${this.state.id}/confirm-mailing-list` : `/center/${this.props.centerId}/microsite/campaign/${this.state.id}/confirm-mailing-list`;
        return href;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    isValid = () => {
        return this.state.assignedColumns.filter(x => x == '' || x == '-1').length == 0;
    }

    render = () => {
        const isValid = this.isValid();

        return (
            <>
                <Row>
                    
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 25 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>Campaign Mailing List Mapping</h1>
                            <div className='pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>
                        {this.state.columnsFromFile.length < 7 ? <Alert bsStyle='danger'>The uploaded CSV file does not meet the required number of fields.</Alert> : <>
                            <Well>
                                <Row>
                                    <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign:</Col>
                                    <Col xs={12} lg={8}>{this.state.campaignName}</Col>
                                </Row>

                                <Row>
                                    <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign Type:</Col>
                                    <Col xs={12} lg={8}>Mailed Out</Col>
                                </Row>

                                <Row>
                                    <Col xs={12} sm={4} componentClass={ControlLabel}>NKR will process the mailing:</Col>
                                    <Col xs={12} lg={8}>{this.state.processedByNkr ? 'Yes' : 'No'}</Col>
                                </Row>
                            </Well>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <div style={{ marginRight: 10 }}>First row contain column headers?</div>
                                <div>
                                    <label className='radio-inline'>
                                        <input
                                            type='radio'
                                            id='hasColumnHeadersYes'
                                            name="hasColumnHeaders"
                                            checked={this.state.hasColumnHeaders}
                                            onClick={() => this.handleHasColumnHeadersChange(true)}
                                        /> Yes
                                    </label>
                                    <label className='radio-inline'>
                                        <input
                                            type='radio'
                                            id='hasColumnHeadersNo'
                                            name="hasColumnHeaders"
                                            checked={!this.state.hasColumnHeaders}
                                            onClick={() => this.handleHasColumnHeadersChange(false)}
                                        /> No
                                    </label>
                                </div>
                            </div>

                            <TableWrapper condensed bordered>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>Column From File</th>
                                        <th>First Row of Data</th>
                                        <th>Map To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.columnsFromFile.map((column, index) => {
                                        return (
                                            <tr key={'col-' + index}>
                                                <td>{this.state.hasColumnHeaders ? column : `Column ${index + 1}`}</td>
                                                <td>{this.state.sampleRow[index]}</td>
                                                <td>
                                                    <FormControl
                                                        componentClass='select'
                                                        value={this.state.assignedColumns[index]}
                                                        onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => this.handleAssignedColumnChange(index, e.currentTarget.value)}
                                                    >
                                                        <option value={-1}>- Assign Column -</option>
                                                        {columnMap.map((mapColumn, colMapIndex) =>
                                                            <option key={'colmap-' + colMapIndex} value={mapColumn}>{mapColumn}</option>
                                                        )}
                                                    </FormControl>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </TableWrapper>

                            <Well bsSize='sm'>
                                <CampaignSteps currentStep={2} campaignId={this.props.campaignId} />
                            </Well>
                        </>}
                        <FormGroup>
                            <div className='col-xs-12 col-lg-12 text-center'>
                                <Button bsStyle='primary' style={{ marginRight: 5 }} disabled={!isValid} onClick={() => this.handleSubmit(true)}>Submit &amp; Continue</Button>
                                <Button disabled={!isValid} onClick={() => this.handleSubmit(false)}>Submit &amp; Return to the Campaign List</Button>
                                <Button bsStyle='link' onClick={this.handleCancel}>Cancel &amp; Return to the Campaign List</Button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getCampaign: (id) => { return dispatch(MicrositeActionCreators.getCampaign(id, true)); },
        saveCampaignMailingListMapping: (id, hasColumnHeaders, columns) => { return dispatch(MicrositeActionCreators.saveCampaignMailingListMapping(id, hasColumnHeaders, columns)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CampaignMailingListMapping);